import { Advertisement, Biz } from "@/components/main/googlemapbanner";
import { fetchAutoRefresh } from "@/lib/middleware/TokenAutoRefresh";
import mongoose from "mongoose";
export const BASE_URL = process.env.NEXT_PUBLIC_BASE_URL;

// post api-Create Business Details API
export const insertbusinessDetailsApi = async (businessInfo: {
  businessname: string;
  categoryId: string[];
  businessdescription: string;
  phonenumber: number;
  websitename: string;
  companyemail: string;
  accessToken: string;
}) => {
  const { accessToken, ...businessPayload } = businessInfo;

  const res = await fetchAutoRefresh(`${BASE_URL}/business`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(businessPayload),
  });

  const results = await res.json();

  if (!res.ok) {
    throw new Error(results.error || "Business Creation failed");
  }

  return results;
};

// get api-Fetch all businesses API
export const fetchallBusinessApi = async (page?: 1, limit?: number) => {
  try {
    const res = await fetch(
      `${BASE_URL}/fetchallbusinesses?page=${page}&limit=${limit}`,
      {
        cache: "no-store",
        method: "GET",
        headers: { "Content-Type": "application/json" },
      },
    );
    const results = await res.json();
    if (!res.ok) {
      throw new Error(results.error || "Failed to fetch business data");
    }
    return results;
  } catch (err) {
    console.error("Fetch API error:", err);
    throw err;
  }
};

//post api-Insert Address details with businessID API
export const insertAddressDetailsApi = async (
  businessId: string,
  data: {
    country: string;
    state: string;
    city: string;
    pincode: number;
    streetaddress: string;
    latitude: number;
    longitude: number;
  },
) => {
  const res = await fetch(`${BASE_URL}/address/${businessId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...data }),
  });

  const result = await res.json();
  return result;
};

//post api-Insert business hours with businessID API
export const insertOpeningHoursApi = async (
  businessId: string,
  payload: {
    openinghours: Record<string, { open: string; close: string }>;
    bookingModes: ("full-day" | "slot" | "none")[];
    slotDuration?: number;
  },
) => {
  const res = await fetch(`${BASE_URL}/openinghours/${businessId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  return await res.json();
};

//get api-Fetch single data of business
export const fetchBusinessById = async (businessId: string) => {
  try {
    const res = await fetch(`${BASE_URL}/fetchsinglebusiness/${businessId}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      cache: "no-store",
    });

    if (!res.ok) {
      throw new Error("Failed to fetch business");
    }

    const data = await res.json();
    return data;
  } catch (error) {
    console.error("Error fetching single business", error);
    return null;
  }
};

//post api-Update full form
export const UpdateFormApi = async (
  businessId: string,
  info: {
    businessname: string;
    categoryId: mongoose.Types.ObjectId | string;
    businessdescription: string;
    phonenumber: number;
    websitename: string;
    companyemail: string;
    country: string;
    state: string;
    city: string;
    pincode: number;
    streetaddress: string;
    openinghours: string;
  },
) => {
  const res = await fetch(`${BASE_URL}/editbusiness/${businessId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(info),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.error || "Updation failed");
  }
  return data;
};

// delete api-soft-delete Business API Call
export const deleteBusinessApi = async (businessId: string) => {
  try {
    const res = await fetch(`${BASE_URL}/deletebusiness/${businessId}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
    });
    const result = await res.json();

    if (!res.ok) {
      throw new Error(result.error || "Failed to delete business");
    }

    return result;
  } catch (error) {
    console.error("Error deleting business:", error);
    throw error;
  }
};

export const updateBusinessLogo = async (businessId: string, logo: File) => {
  try {
    const formData = new FormData();
    formData.append("logo", logo);

    try {
      const res = await fetch(`${BASE_URL}/images/${businessId}/logo`, {
        method: "POST",
        body: formData,
      });

      const result = await res.json();

      return {
        ...result,
        ok: res.ok,
      };
    } catch (error) {
      console.error("Error uploading logo:", error);
      return {
        ok: false,
        error: "Network error or server unavailable",
      };
    }
  } catch (error) {
    console.error("error in logo uploading");
  }
};

//imagekitt.io getbusiness logo
export const getBusinessLogo = async (
  businessId: string,
): Promise<{
  ok: boolean;
  error?: string;
  logo?: { fileId: string; url: string };
}> => {
  try {
    const res = await fetch(`${BASE_URL}/images/${businessId}/logo`, {
      method: "GET",
    });

    const result = await res.json();
    if (!res.ok) {
      return {
        ok: false,
        error: result?.error || "Failed to fetch logo",
      };
    }

    return {
      ok: true,
      logo: result.logo, // This backend
    };
  } catch (error) {
    console.error("Error fetching logo:", error);
    return {
      ok: false,
      error: "Network error or server unavailable",
    };
  }
};

export const fetchMultipleBusinessLogos = async (
  businessIds: string[],
): Promise<{
  ok: boolean;
  logos?: { businessId: string; logo: string | null }[];
  error?: string;
}> => {
  try {
    const res = await fetch(`${BASE_URL}/images/logos`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ businessIds }),
    });

    const result = await res.json();

    if (!res.ok) {
      return {
        ok: false,
        error: result?.error || "Failed to fetch logos",
      };
    }

    return {
      ok: true,
      logos: result.logos,
    };
  } catch (error) {
    console.error("Error in fetchMultipleBusinessLogos:", error);
    return {
      ok: false,
      error: "Network error or server unavailable",
    };
  }
};

// direct upload images to imagekit

export const updateStoreImage = async (
  businessId: string,
  files: File[],
  onProgress?: (index: number, progress: number) => void,
): Promise<ImageKitUploadResult[]> => {
  if (!files.length) {
    return [];
  }

  const results: ImageKitUploadResult[] = [];

  for (let index = 0; index < files.length; index++) {
    const file = files[index];

    if (!file.type.startsWith("image/")) {
      throw new Error(`${file.name} is not a valid image file.`);
    }

    const result = await uploadToImageKit({
      file,
      folder: "/stores/images",
      onProgress: (progress) => {
        onProgress?.(index, progress);
      },
    });

    results.push(result);
  }

  return results;
};
export const uploadBannerDirect = async (
  file: File,
  onProgress?: (progress: number) => void,
): Promise<ImageKitUploadResult> => {
  if (!file.type.startsWith("image/")) {
    throw new Error("Only image files are allowed for bannerImage.");
  }

  return uploadToImageKit({
    file,
    folder: "/stores/banners",
    onProgress,
  });
};
// direct upload Store Video
export const updateStoreVideo = async (
  businessId: string,
  file: File,
  onProgress?: (progress: number) => void,
): Promise<ImageKitUploadResult> => {
  if (!file.type.startsWith("video/")) {
    throw new Error("Only video files are allowed for storeVideo.");
  }

  const MAX_VIDEO_SIZE = 100 * 1024 * 1024;

  if (file.size > MAX_VIDEO_SIZE) {
    throw new Error("Video must be smaller than 100MB.");
  }

  return uploadToImageKit({
    file,
    folder: "/stores/videos",
    onProgress,
  });
};

// Upload Images + Video Together
export const updateStoreMedia = async (
  businessId: string,
  storeImages: File[] = [],
  video?: File | null,
) => {
  try {
    const formData = new FormData();

    // Images
    storeImages.forEach((file) => {
      formData.append("storeImages", file);
    });

    // Video
    if (video) {
      formData.append("storeVideo", video);
    }

    const response = await fetch(
      `${BASE_URL}/images/${businessId}/storeimages`,
      {
        method: "POST",
        body: formData,
      },
    );

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || "Failed to upload store media");
    }

    return result;
  } catch (error) {
    console.error("Error uploading store media:", error);

    throw error;
  }
};
interface BusinessMediaPayload {
  storeImages?: {
    url: string;
    fileId: string;
    fileName?: string;
  }[];

  bannerImage?: {
    url: string;
    fileId: string;
    fileName?: string;
  };

  storeVideo?: {
    url: string;
    fileId: string;
    fileName?: string;
  };
}

export const updateBusinessMedia = async (
  businessId: string,
  media: BusinessMediaPayload,
) => {
  try {
    const response = await fetch(
      `${BASE_URL}/images/${businessId}/storeimages`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(media),
      },
    );

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result?.error || "Failed to update business media");
    }

    return result;
  } catch (error) {
    console.error("Error updating business media:", error);
    throw error;
  }
};
export const getStoreMedia = async (businessId: string) => {
  try {
    const response = await fetch(
      `${BASE_URL}/images/${businessId}/storeimages`,
      {
        method: "GET",
        cache: "no-store",
      },
    );

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || "Failed to fetch store media");
    }

    return {
      storeImages: result.storeImages || [],
      storeVideo: result.storeVideo || null,
      storeBanner: result.bannerImage || null,
    };
  } catch (error) {
    console.error("Error fetching store media:", error);

    throw error;
  }
};

export const getStoreImages = async (businessId: string) => {
  try {
    const response = await fetch(
      `${BASE_URL}/images/${businessId}/storeimages`,
      {
        method: "GET",
        cache: "no-store",
      },
    );

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || "Failed to fetch store images");
    }

    return result.storeImages || [];
  } catch (error) {
    console.error("Error fetching store images:", error);

    throw error;
  }
};

export const getStoreVideo = async (businessId: string) => {
  try {
    const response = await fetch(
      `${BASE_URL}/images/${businessId}/storeimages`,
      {
        method: "GET",
        cache: "no-store",
      },
    );

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || "Failed to fetch store video");
    }

    return result.storeVideo || null;
  } catch (error) {
    console.error("Error fetching store video:", error);

    throw error;
  }
};

export const deleteStoreImages = async (
  businessId: string,
  fileIds: string[],
) => {
  try {
    const response = await fetch(
      `${BASE_URL}/images/${businessId}/storeimages`,
      {
        method: "DELETE",

        headers: {
          "Content-Type": "application/json",
        },

        body: JSON.stringify({
          fileIds,
        }),
      },
    );

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || "Failed to delete store images");
    }

    return result;
  } catch (error) {
    console.error("Error deleting store images:", error);

    throw error;
  }
};

export const deleteStoreVideo = async (businessId: string) => {
  try {
    const response = await fetch(
      `${BASE_URL}/images/${businessId}/storeimages`,
      {
        method: "DELETE",

        headers: {
          "Content-Type": "application/json",
        },

        body: JSON.stringify({
          deleteVideo: true,
        }),
      },
    );

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || "Failed to delete store video");
    }

    return result;
  } catch (error) {
    console.error("Error deleting store video:", error);

    throw error;
  }
};
export const deleteAllStoreMedia = async (businessId: string) => {
  try {
    const response = await fetch(
      `${BASE_URL}/images/${businessId}/storeimages`,
      {
        method: "DELETE",

        headers: {
          "Content-Type": "application/json",
        },

        body: JSON.stringify({
          deleteAllImages: true,
          deleteVideo: true,
        }),
      },
    );

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || "Failed to delete store media");
    }

    return result;
  } catch (error) {
    console.error("Error deleting store media:", error);

    throw error;
  }
};

import { upload } from "@imagekit/javascript";

export interface ImageKitUploadResult {
  url: string;
  fileId: string;
  fileName?: string;
  width?: number;
  height?: number;
  size?: number;
  filePath?: string;
}

interface UploadOptions {
  file: File;
  folder: string;
  fileName?: string;
  onProgress?: (progress: number) => void;
}

const getImageKitAuth = async () => {
  const response = await fetch(`${BASE_URL}/images/auth`, {
    method: "GET",
    credentials: "include",
    cache: "no-store",
  });

  if (!response.ok) {
    const text = await response.text();

    throw new Error(text || "Failed to get ImageKit authentication parameters");
  }

  return response.json();
};

const sanitizeFileName = (fileName: string) => {
  return fileName.replace(/[^a-zA-Z0-9._-]/g, "_");
};

export const uploadToImageKit = async ({
  file,
  folder,
  fileName,
  onProgress,
}: UploadOptions): Promise<ImageKitUploadResult> => {
  if (!file) {
    throw new Error("File is required");
  }

  const auth = await getImageKitAuth();

  const safeFileName = sanitizeFileName(fileName || file.name);

  const result = await upload({
    file,
    fileName: safeFileName,

    publicKey: auth.publicKey,
    token: auth.token,
    signature: auth.signature,
    expire: auth.expire,

    folder,

    useUniqueFileName: true,

    onProgress: (event: ProgressEvent) => {
      if (!event.total) {
        return;
      }

      const percentage = Math.round((event.loaded / event.total) * 100);

      onProgress?.(percentage);
    },
  });

  return {
    url: result.url,
    fileId: result.fileId,
    fileName: result.name,
    width: result.width,
    height: result.height,
    size: result.size,
    filePath: result.filePath,
  };
};

//post api-for insert review
export const addReviewForBusinessApi = async (
  businessId: string,
  reviewData: {
    title: string;
    img_url: string;
    link: string;
    active: boolean;
    rating: number;
  },
) => {
  const res = await fetch(`${BASE_URL}/reviews/${businessId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(reviewData),
  });

  const result = await res.json();

  if (!res.ok) {
    throw new Error(result.error || "Failed to add review");
  }

  return result.data;
};

//get api- get review by businessId
export const getReviewsByBusinessIdApi = async (businessId: string) => {
  const res = await fetch(`${BASE_URL}/reviews/${businessId}`);
  const result = await res.json();

  if (!res.ok) {
    throw new Error(result.error || "Failed to fetch reviews");
  }

  return result.data;
};

//post api-to insert feedback
export const postFeedback = async (
  businessId: string,
  formData: {
    name: string;
    email: string;
    message: string;
    rating?: number;
    ownerId: string;
  },
) => {
  try {
    const res = await fetch(`${BASE_URL}/feedback/${businessId}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
    });

    const data = await res.json();

    if (data.success) {
      alert("Thank you for your feedback!");
    } else {
      console.error("Error submitting feedback:", data.message);
      alert("Something went wrong.");
    }
  } catch (error) {
    console.error("API error:", error);
    alert("Server error.");
  }
};

//get api-fetch the feedback by businessId
export const fetchFeedbacks = async (businessId: string) => {
  try {
    const res = await fetch(`${BASE_URL}/feedback/${businessId}`);
    const data = await res.json();

    if (data.success) {
    } else {
      console.error("Error fetching feedbacks:", data.message);
    }
  } catch (error) {
    console.error("API error:", error);
  }
};

export interface BusinessLocation {
  coordinates: [any, any];
  latitude: number;
  longitude: number;
}

export interface Business {
  _id: string;
  businessname: string;
  businessdescription: string;
  location: BusinessLocation;
  categoryInfo?: string;
  categoryId?: string[];
  websitename: string;
  [key: string]: any;
}

export interface NearbyBusinessResponse {
  nearbyDBBusinesses: Biz[];
  advertisements: Advertisement[];
}

export const getNearbyBusinesses = async (
  latitude: number,
  longitude: number,
): Promise<NearbyBusinessResponse> => {
  try {
    const response = await fetch(`${BASE_URL}/nearbybusinesses/aroundme`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ latitude, longitude }),
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching businesses:", error);
  }
};

//post-api for inserting the social media links
export const postSocialMediaLinks = async (
  businessId: string,
  socialMediaData: {
    instagram?: string;
    facebook?: string;
  },
) => {
  const response = await fetch(
    `${BASE_URL}/business/sociallinks/${businessId}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(socialMediaData),
    },
  );

  const result = await response.json();
  if (!response.ok) throw new Error(result.error || "Failed to update");

  return result;
};

export const recordBusinessClickApi = async (businessId: string) => {
  try {
    const res = await fetch(`${BASE_URL}/business/clicks/${businessId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });

    const result = await res.json();

    if (!res.ok) {
      throw new Error(result.error || "Failed to record business click");
    }

    return result;
  } catch (error) {
    console.error("Error recording business click:", error);
    throw error;
  }
};

export const getBusinessStatsApi = async (businessId: string) => {
  const res = await fetch(`${BASE_URL}/business/stats/${businessId}`);
  const result = await res.json();
  if (!res.ok) throw new Error(result.error || "Failed to fetch stats");
  return result;
};

//fetch business by slug
export const getBusinessBySlug = async (slug: string) => {
  try {
    const resp = await fetch(`${BASE_URL}/business/${slug}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      cache: "no-store",
    });

    if (!resp.ok) {
      throw new Error("Failed to fetch business by slug");
    }

    const data = await resp.json();
    return data.business;
  } catch (error) {
    console.error("Error fetching single business", error);
    return null;
  }
};

interface BusinessSearchResponse {
  success: boolean;
  businesses: Business[];
  categories: string[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
}
//fetch business by catgory location description and name
export const getBusinessesByKeyword = async (
  keyword: string,
  page: number = 1,
  limit: number = 10,
  categories?: string | string[],
  latitude?: number,
  longitude?: number,
): Promise<BusinessSearchResponse | null> => {
  try {
    const params = new URLSearchParams({
      keyword: keyword.trim(),
      page: page.toString(),
      limit: limit.toString(),
    });

    // Add coordinates only when available
    if (
      latitude !== undefined &&
      longitude !== undefined &&
      Number.isFinite(latitude) &&
      Number.isFinite(longitude)
    ) {
      params.append("latitude", latitude.toString());
      params.append("longitude", longitude.toString());
    }

    if (categories) {
      let categoriesArray: string[];

      if (typeof categories === "string") {
        const trimmed = categories.trim();

        categoriesArray = trimmed ? [trimmed] : [];
      } else {
        categoriesArray = categories.filter((c) => c.trim() !== "");
      }

      if (categoriesArray.length > 0) {
        params.append("categories", categoriesArray.join(","));
      }
    }

    const url = `${BASE_URL}/business/businesssearchbykeywords?${params.toString()}`;

    console.log("SEARCH URL:", url);

    const resp = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      cache: "no-store",
    });

    if (!resp.ok) {
      throw new Error(
        `Failed to fetch businesses: ${resp.status} ${resp.statusText}`,
      );
    }

    const data = await resp.json();

    return data as BusinessSearchResponse;
  } catch (error) {
    console.error("Error fetching businesses by keyword:", error);

    return null;
  }
};

export const getCategoriesWithBusiness = async (page?: 1, limit?: number) => {
  try {
    const res = await fetch(
      `${BASE_URL}/business/categorieswithbusiness?page=${page}&limit=${limit}`,
      {
        cache: "no-store",
        method: "GET",
        headers: { "Content-Type": "application/json" },
      },
    );
    const results = await res.json();
    if (!res.ok) {
      throw new Error(results.error || "Failed to fetch business data");
    }
    return results;
  } catch (err) {
    console.error("Fetch API error:", err);
    throw err;
  }
};
